import { usePickerFlyoutRow } from "./PickerChainContext";
import { usePickerRoot } from "./PickerContext";
import { usePickerCascadeRow } from "./usePickerCascadeRow";
import type { PickerItemValue } from "./Picker.types";

/**
 * What activating a row does. A cascade parent is an action row: its click
 * toggles the leaves below. Inside `Picker.Flyout` the row opens its level
 * instead of selecting, unless the parent selects (its own value, or its
 * leaves under cascade): then the row keeps selecting and the triangle opens
 * the level. Anything else runs the caller's `onSelect`, if any.
 */
export const usePickerItemActivation = ({
    value,
    onSelect: onSelectProp,
    closeOnSelect: closeOnSelectProp,
}: {
    value: PickerItemValue | undefined;
    onSelect: (() => void) | undefined;
    closeOnSelect: boolean;
}) => {
    const root = usePickerRoot();
    const cascade = usePickerCascadeRow(value);
    const flyoutRow = usePickerFlyoutRow();
    const isSelectableFlyoutParent =
        flyoutRow !== null &&
        value !== undefined &&
        (root.isParentSelectable || cascade !== null);
    let onSelect = onSelectProp;
    if (cascade !== null) {
        onSelect = cascade.toggle;
    } else if (flyoutRow !== null && !isSelectableFlyoutParent) {
        onSelect = flyoutRow.open;
    }
    return {
        cascade,
        flyoutRow,
        /** The flyout row when the row itself selects and its triangle opens the level. */
        selectableFlyoutRow: isSelectableFlyoutParent ? flyoutRow : null,
        onSelect,
        closeOnSelect:
            flyoutRow === null && cascade === null ? closeOnSelectProp : false,
    };
};
